import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, balanceEntries, fmtDate, fmtDateTime, STATUS_META } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, TrendingUp, Receipt, Clock, CalendarClock, ArrowRight, Coins, HandCoins, FileText } from "lucide-react";

function MoneyList({ obj }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
      {balanceEntries(obj).map(([cur, v]) => (
        <span key={cur} className="font-mono font-semibold">{fmtMoney(v, cur)}</span>
      ))}
    </div>
  );
}

function SummaryCard({ title, obj, icon: Icon, hero, tone }) {
  return (
    <div data-testid={`summary-${title}`}
      className={`rounded-2xl p-5 border ${hero ? "bg-navy-900 border-navy-900 text-white" : "bg-white border-slate-200"} shadow-[0_4px_20px_rgba(0,0,0,0.03)]`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-semibold uppercase tracking-wider ${hero ? "text-slate-300" : "text-slate-500"}`}>{title}</span>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${hero ? "bg-white/10" : tone || "bg-slate-100"}`}>
          <Icon className={`w-4 h-4 ${hero ? "text-white" : "text-navy-900"}`} />
        </span>
      </div>
      <div className={`text-lg ${hero ? "text-white" : "text-navy-900"}`}><MoneyList obj={obj} /></div>
    </div>
  );
}

const ACT_ICON = { advance: Coins, expense: Receipt, collection: HandCoins, document: FileText };

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => { api.get("/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { load(); window.addEventListener("shb:refresh", load); return () => window.removeEventListener("shb:refresh", load); }, [load]);

  const openEk = (id) => navigate(`/muvekkiller/${id}?ekavans=1`);

  if (!d) return <div className="py-20 text-center text-slate-400">Yükleniyor…</div>;

  return (
    <div className="space-y-6 animate-in-up">
      <div>
        <p className="text-sm text-slate-500">Merhaba, {user?.name}</p>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">Kontrol Paneli</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <SummaryCard title="Toplam Avans Bakiyesi" obj={d.total_advance_balance} icon={Coins} hero />
        <SummaryCard title="Bu Ay Tahsil Edilen Vekâlet" obj={d.month_collections} icon={TrendingUp} tone="bg-emerald-100" />
        <SummaryCard title="Bu Ay Yapılan Masraf" obj={d.month_expenses} icon={Receipt} tone="bg-amber-100" />
        <SummaryCard title="Bekleyen Ödemeler" obj={d.pending_fees} icon={Clock} tone="bg-slate-100" />
        <SummaryCard title="Geciken Ödemeler" obj={d.overdue_fees} icon={AlertTriangle} tone="bg-brand-redbg" />
      </div>

      {/* Critical advance clients */}
      {d.critical_clients.length > 0 && (
        <div data-testid="critical-clients-card" className="rounded-2xl bg-brand-red text-white p-5 shadow-[0_10px_40px_rgba(163,38,56,0.25)]">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5" />
            <h2 className="font-heading text-lg font-semibold">Avansı Kritik Seviyede Olan Müvekkiller</h2>
          </div>
          <div className="space-y-2">
            {d.critical_clients.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 bg-white/10 rounded-xl px-4 py-3">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{c.name}</p>
                  <p className="text-xs text-white/80 font-mono">{c.client_no} · Mevcut: {fmtMoney(c.current)} · Min: {fmtMoney(c.min_limit)}</p>
                  <p className="text-[11px] text-white/90 mt-0.5">Eksik: {fmtMoney(c.missing)} · {c.status === "AVANS_TUKENDI" ? "Avans Tükendi" : "Ek Avans Talep Edilmeli"}</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button data-testid={`crit-open-${c.id}`} onClick={() => navigate(`/muvekkiller/${c.id}`)} className="text-xs bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5 transition-colors">Aç</button>
                  <button data-testid={`crit-ekavans-${c.id}`} onClick={() => openEk(c.id)} className="text-xs bg-white text-brand-red font-semibold rounded-lg px-3 py-1.5 hover:bg-white/90 transition-colors">Ek Avans</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming payments */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="w-5 h-5 text-navy-900" />
            <h2 className="font-heading text-lg font-semibold text-navy-900">Yaklaşan Ödemeler</h2>
            <span className="text-xs text-slate-400">30 gün</span>
          </div>
          {d.upcoming_payments.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Yaklaşan ödeme yok.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {d.upcoming_payments.map((p) => (
                <button key={p.id} onClick={() => navigate(`/muvekkiller/${p.client_id}`)}
                  className="w-full flex items-center justify-between py-3 text-left hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                  <div>
                    <p className="text-sm font-medium text-navy-900">{p.client_name}</p>
                    <p className="text-xs text-slate-400">{p.description || "Vekâlet ücreti"} · Vade {fmtDate(p.due_date)}</p>
                  </div>
                  <span className="font-mono font-semibold text-navy-900 text-sm">{fmtMoney(p.amount, p.currency)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <h2 className="font-heading text-lg font-semibold text-navy-900 mb-4">Son Hareketler</h2>
          {d.recent_activities.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Henüz hareket yok.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {d.recent_activities.map((a, i) => {
                const Icon = ACT_ICON[a.kind] || FileText;
                return (
                  <div key={i} className="flex items-center gap-3 py-2.5">
                    <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-navy-900" /></span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy-900 truncate">{a.label}</p>
                      <p className="text-xs text-slate-400 truncate">{a.client_name} · {fmtDateTime(a.at)}</p>
                    </div>
                    {a.amount != null && <span className="font-mono text-sm text-navy-900">{fmtMoney(a.amount, a.currency)}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
